import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Konva from "konva";
import { Play, Pause, Volume2, VolumeX, Maximize2, Minimize2, X } from "lucide-react";
import { TimelineProvider, useTimeline } from "../../timeline/EditorStore";
import { syncElementVisibilityAtPlayhead, syncVideosAtPlayhead, syncVideoAudioAtPlayhead, syncAudioClipsAtPlayhead, type AudioMap, cleanupUnusedAudioElements, resetAudioSyncState, resetVideoSyncState } from "../../timeline/engine";
import type { CameraTrack, Element } from "../../types";
import { CanvasStage } from "../../canvas";
import { createDefaultCameraTrack, getCameraTransformAtTime, getCameraTransformAtTimeAdvanced } from "../../engine/cameraEngine";
import type { ClipRange } from "../../engine/animationRuntime";
import { isBodyFxRuntimeScanEnabled, isCameraMotionV2Enabled } from "../../utils/studioFeatureFlags";
import { cancelSingletonRafRequest, releaseSingletonRafLoop, requestSingletonRafLoop } from "../../utils/singleLoopGuard";
import { useWebCodecsSelectedClipPreview } from "../../webcodecs/useWebCodecsSelectedClipPreview";
import {
  applyFxPacketsToNode,
  captureFxNodeBaseState,
  computeFxRenderPackets,
  packetIsBackgroundBlur,
  packetNeedsBodyTracking,
  packetWantsSegmentation,
  restoreFxNodeState,
  type FxNodeBaseState,
  type FxQualityTier,
  type FxRenderPacket,
} from "../../timeline/fxRuntime";
import { FxAudioFeatureAnalyzer } from "../../timeline/fxAudioAnalysis";
import { FxBodyTracker } from "../../timeline/fxBodyTracking";
import { FxBodySegmenter } from "../../timeline/fxBodySegmentation";
import {
  getFxSegmentationRuntimeQualityTier,
  subscribeFxSegmentationTuning,
} from "../../timeline/fxSegmentationTuning";
import { FxQualityTierController } from "../../timeline/fxQualityTierController";
import { FxFpsTelemetry } from "../../timeline/fxFpsTelemetry";
import {
  resolveFxVideoOverlayBudget,
  type FxVideoOverlayBudgetMode,
} from "../../timeline/fxOverlayPlaybackBudget";
import {
  cleanupBackgroundBlurOverlays,
  disposeBackgroundBlurOverlays,
  syncBackgroundBlurOverlay,
  type FxBackgroundOverlayMap,
} from "../../timeline/fxBackgroundSeparation";
import {
  cleanupFxGpuOverlays,
  disposeFxGpuOverlays,
  syncFxGpuOverlay,
  type FxGpuOverlayMap,
} from "../../timeline/fxGpuOverlays";
import {
  cleanupFxVideoOverlays,
  disposeFxVideoOverlays,
  syncFxVideoOverlays,
  type FxVideoOverlayMap,
} from "../../timeline/fxVideoOverlays";
import {
  getStoredTimelineDensityMode,
  subscribeTimelineDensityMode,
  type TimelineDensityMode,
} from "../../timeline/timelineDensity";
import {
  getStoredDraftPreviewMode,
  subscribeDraftPreviewMode,
  type DraftPreviewMode,
} from "../../timeline/draftPreviewMode";
// New clean transition system
import {
  type TransitionEffect,
  type ClipTiming,
  isPlayheadInGap,
  findTransitionForClip,
  getClipTransitionEffect,
} from "../../transitions";
import {
  readStudioPreviewSnapshot,
  STUDIO_PREVIEW_SNAPSHOT_KEY,
  type StudioPreviewSnapshotV1,
} from "../../utils/previewSnapshot";
import { resolveRuntimeVideoHotSet } from "../../editor2/runtimeMediaHotSet";
import { markDirty, setRenderLayer, setRenderPlaying } from "../../engine/RenderCoordinator";

type RuntimeMedia = {
  imageMap: Map<string, HTMLImageElement>;
  videoMap: Map<string, HTMLVideoElement>;
  audioMap: AudioMap;
  webCodecsCanvasMap: Map<string, HTMLCanvasElement>;
};

function clamp(x: number, a: number, b: number) {
  return Math.max(a, Math.min(b, x));
}

const PLAYHEAD_WINDOW_EPS = 1 / 240;
const PLAYHEAD_SOFT_HITCH_DELTA_SEC = 0.45;
const PLAYHEAD_HARD_SEEK_DELTA_SEC = 1.0;
const PLAYHEAD_LOOP_EDGE_TOLERANCE_SEC = 0.12;

type PlayheadDiscontinuityKind =
  | "none"
  | "loop-wrap-soft"
  | "playback-hitch-soft"
  | "seek-hard";

type PlayheadDiscontinuityInfo = {
  kind: PlayheadDiscontinuityKind;
  delta: number;
};

function detectPlayheadDiscontinuity(args: {
  playhead: number;
  prevPlayhead: number;
  isPlaying: boolean;
  loopPlayback: boolean;
  loopRegion: { start: number; end: number } | null;
  durationSec: number;
}): PlayheadDiscontinuityInfo {
  const { playhead, prevPlayhead, isPlaying, loopPlayback, loopRegion, durationSec } = args;
  const delta = playhead - prevPlayhead;
  const absDelta = Math.abs(delta);

  if (!isPlaying) {
    if (absDelta >= PLAYHEAD_HARD_SEEK_DELTA_SEC) return { kind: "seek-hard", delta };
    return { kind: "none", delta };
  }

  if (delta < -PLAYHEAD_WINDOW_EPS) {
    const loopStart =
      loopPlayback && loopRegion && loopRegion.end > loopRegion.start + 0.01
        ? Math.max(0, loopRegion.start)
        : 0;
    const loopEnd =
      loopPlayback && loopRegion && loopRegion.end > loopRegion.start + 0.01
        ? Math.max(loopStart, loopRegion.end)
        : Math.max(0, durationSec);
    const wrappedNearLoopEdge =
      prevPlayhead >= loopEnd - PLAYHEAD_LOOP_EDGE_TOLERANCE_SEC &&
      playhead <= loopStart + PLAYHEAD_LOOP_EDGE_TOLERANCE_SEC;
    if (wrappedNearLoopEdge) return { kind: "loop-wrap-soft", delta };
    return { kind: "playback-hitch-soft", delta };
  }

  if (absDelta >= PLAYHEAD_SOFT_HITCH_DELTA_SEC) {
    return { kind: "playback-hitch-soft", delta };
  }

  return { kind: "none", delta };
}

function formatTime(sec: number): string {
  const s = Math.max(0, Number.isFinite(sec) ? sec : 0);
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = Math.floor(s % 60);
  if (hh > 0) return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

function useElementRect(ref: React.RefObject<HTMLElement>) {
  const [rect, setRect] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setRect({ width, height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return rect;
}

function StudioPreviewInner() {
  const stageRef = useRef<Konva.Stage>(null);
  const layerRef = useRef<Konva.Layer>(null);
  const selRectRef = useRef<Konva.Rect>(null);
  const trRef = useRef<Konva.Transformer>(null);

  const runtime = useRef<RuntimeMedia>({ imageMap: new Map(), videoMap: new Map(), audioMap: new Map(), webCodecsCanvasMap: new Map() });
  const parkedVideoMapRef = useRef<Map<string, HTMLVideoElement>>(new Map());

  // AUDIO LIFECYCLE FIX: Stop all audio on unmount (route change, component unmount)
  useEffect(() => {
    return () => {
      // Stop all audio clips
      for (const [, audio] of runtime.current.audioMap.entries()) {
        try {
          audio.pause();
          audio.currentTime = 0;
        } catch {}
      }
      // Stop audio from video clips
      for (const [, video] of runtime.current.videoMap.entries()) {
        try {
          video.pause();
          video.muted = true;
        } catch {}
      }
      // Reset sync states
      resetVideoSyncState();
      resetAudioSyncState();
      fxAudioAnalyzerRef.current?.dispose();
      fxAudioAnalyzerRef.current = null;
      fxBodyTrackerRef.current?.dispose();
      fxBodyTrackerRef.current = null;
      fxBodySegmenterRef.current?.dispose();
      fxBodySegmenterRef.current = null;
      fxFpsTelemetryRef.current.dispose();
      disposeBackgroundBlurOverlays(fxBackgroundOverlayRef.current);
      disposeFxGpuOverlays(fxGpuOverlayRef.current);
      disposeFxVideoOverlays(fxVideoOverlayRef.current);
      fxNodeBaseStateRef.current.clear();
      fxRenderPacketsRef.current.clear();
    };
  }, []);

  const {
    assets,
    clips,
    tracks,
    transitions,
    playhead,
    isPlaying,
    loopPlayback,
    loopRegion,
    masterMuted,
    selection,
    setPlayhead,
    setPlaying,
    setLoopPlayback,
    setLoopRegion,
    clearLoopRegion,
    setMasterMuted,
    hydrateFromManifest,
    getActiveFxBlocksAtTime,
  } = useTimeline();

  // ── RenderCoordinator: bind layer and sync playback state ──
  useEffect(() => {
    const layer = layerRef.current;
    if (layer) setRenderLayer(layer);
    return () => setRenderLayer(null);
  }, []);
  useEffect(() => {
    setRenderPlaying(isPlaying);
  }, [isPlaying]);

  const webCodecs = useWebCodecsSelectedClipPreview({
    clips: clips as any,
    assets: assets as any,
    selection,
    playheadSec: playhead,
    isPlaying,
    runtime: runtime as any,
    layerRef,
  });

  const [canvasSize, setCanvasSize] = useState<{ width: number; height: number }>({ width: 1920, height: 1080 });
  const [elements, setElements] = useState<Element[]>([]);
  const [cameraTrack, setCameraTrack] = useState<CameraTrack | undefined>(undefined);

  const allowCameraMotionV2 = isCameraMotionV2Enabled();
  const elementsById = useMemo(() => {
    return new Map(elements.map((el) => [el.id, el]));
  }, [elements]);

  const videoHostRef = useRef<HTMLDivElement>(null);
  const hostRect = useElementRect(videoHostRef);

	  const durationSec = useMemo(() => {
	    return (clips || []).reduce((max: number, c: any) => Math.max(max, Number(c?.end) || 0), 0);
	  }, [clips]);

	  const clipRangesByElementId = useMemo(() => {
	    const map = new Map<string, ClipRange>();
	    for (const c of clips as any[]) {
	      const elId = typeof c?.elementId === "string" ? c.elementId : null;
	      if (!elId) continue;
	      const start = Number(c?.start) || 0;
	      const end = Number(c?.end) || 0;
	      const prev = map.get(elId);
	      if (!prev) map.set(elId, { start, end });
	      else map.set(elId, { start: Math.min(prev.start, start), end: Math.max(prev.end, end) });
	    }
	    return map;
	  }, [clips]);

	  const scale = useMemo(() => {
	    const w = Math.max(1, hostRect.width);
	    const h = Math.max(1, hostRect.height);
	    const cw = Math.max(1, canvasSize.width);
    const ch = Math.max(1, canvasSize.height);
    return clamp(Math.min(w / cw, h / ch), 0.05, 2);
  }, [canvasSize.height, canvasSize.width, hostRect.height, hostRect.width]);

  const didLoadOnceRef = useRef(false);

  const loadFromSnapshot = useCallback((snapshot: StudioPreviewSnapshotV1, opts?: { preserveTransport?: boolean }) => {
    setCanvasSize({ width: snapshot.canvas.width, height: snapshot.canvas.height });
    setElements(snapshot.elements as Element[]);
    setCameraTrack((snapshot as any).cameraTrack);
    hydrateFromManifest({
      assets: snapshot.timeline.assets,
      clips: snapshot.timeline.clips,
      tracks: snapshot.timeline.tracks,
      keyframes: snapshot.timeline.keyframes,
      transitions: snapshot.timeline.transitions,
      effectRegions: snapshot.timeline.effectRegions,
      fxBlocks: snapshot.timeline.fxBlocks,
    });
    setLoopPlayback(!!snapshot.timeline.loopPlayback);
    setMasterMuted(!!snapshot.timeline.masterMuted);
    const lr = (snapshot.timeline as any).loopRegion as { start?: unknown; end?: unknown } | null | undefined;
    if (lr && typeof lr === "object" && Number.isFinite(lr.start as number) && Number.isFinite(lr.end as number)) {
      setLoopRegion(Number(lr.start), Number(lr.end));
    } else {
      clearLoopRegion();
    }
    if (!opts?.preserveTransport) {
      setPlayhead(Math.max(0, Number(snapshot.timeline.playhead) || 0));
    }
  }, [clearLoopRegion, hydrateFromManifest, setLoopPlayback, setLoopRegion, setMasterMuted, setPlayhead]);

  const cameraTransform = useMemo(() => {
    const track = cameraTrack ?? createDefaultCameraTrack();
    const time = Number.isFinite(playhead) ? playhead : 0;
    return allowCameraMotionV2
      ? getCameraTransformAtTimeAdvanced(track, time, {
        allowAdvanced: true,
        context: { canvasW: canvasSize.width, canvasH: canvasSize.height, elementsById },
      })
      : getCameraTransformAtTime(track, time);
  }, [allowCameraMotionV2, cameraTrack, canvasSize.height, canvasSize.width, elementsById, playhead]);
  const previewVideoHotSetPlan = useMemo(
    () =>
      resolveRuntimeVideoHotSet({
        elements,
        clips: clips as Array<{ elementId?: string | null; start: number; end: number }>,
        selectedIds: [],
        playhead,
        isPlaying,
      }),
    [clips, elements, isPlaying, playhead]
  );

  const tryLoadSnapshot = useCallback(() => {
    const snap = readStudioPreviewSnapshot();
    if (!snap) return false;
    loadFromSnapshot(snap, { preserveTransport: didLoadOnceRef.current });
    didLoadOnceRef.current = true;
    return true;
  }, [loadFromSnapshot]);

  // Initial snapshot load + cross-window updates (editor writes to localStorage on Preview click).
  useEffect(() => {
    tryLoadSnapshot();
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STUDIO_PREVIEW_SNAPSHOT_KEY) return;
      tryLoadSnapshot();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [tryLoadSnapshot]);

  // Runtime media (images/videos) for Konva rendering.
  useEffect(() => {
    const layer = layerRef.current;
    const draw = () => markDirty();

    const desiredImageIds = new Set<string>();
    const validVideoIds = new Set<string>();
    const hotVideoIds = new Set(previewVideoHotSetPlan.hotIds);

    const hydrateVideo = (video: HTMLVideoElement, src: string) => {
      const typed = video as HTMLVideoElement & { __vbSrc?: string; __vbHydrated?: boolean };
      const previousSrc = typed.__vbSrc;
      typed.__vbSrc = src;
      video.preload = "metadata";
      if (typed.__vbHydrated === true && previousSrc === src) return;
      video.src = src;
      video.load();
      typed.__vbHydrated = true;
    };

    const parkVideo = (id: string) => {
      const active = runtime.current.videoMap.get(id);
      if (!active) return;
      runtime.current.videoMap.delete(id);
      parkedVideoMapRef.current.set(id, active);
      try { active.pause(); } catch {}
      try { active.currentTime = 0; } catch {}
      try { active.removeAttribute("src"); active.load(); } catch {}
      (active as HTMLVideoElement & { __vbHydrated?: boolean }).__vbHydrated = false;
    };

    const ensureVideo = (id: string, src: string) => {
      const existing = runtime.current.videoMap.get(id) ?? parkedVideoMapRef.current.get(id);
      if (existing) {
        (existing as HTMLVideoElement & { __vbSrc?: string }).__vbSrc = src;
        parkedVideoMapRef.current.delete(id);
        runtime.current.videoMap.set(id, existing);
        hydrateVideo(existing, src);
        return existing;
      }

      const v = document.createElement("video");
      (v as HTMLVideoElement & { __vbSrc?: string; __vbHydrated?: boolean }).__vbSrc = src;
      (v as HTMLVideoElement & { __vbHydrated?: boolean }).__vbHydrated = false;
      v.crossOrigin = "anonymous";
      v.muted = true;
      v.playsInline = true;
      v.preload = "metadata";

      const onAny = () => draw();
      v.addEventListener("loadedmetadata", onAny);
      v.addEventListener("loadeddata", onAny);
      v.addEventListener("seeked", onAny);
      v.addEventListener("timeupdate", onAny);
      v.addEventListener("error", onAny);

      runtime.current.videoMap.set(id, v);
      hydrateVideo(v, src);
      return v;
    };

    for (const el of elements) {
      if (!el || typeof el !== "object") continue;
      if (el.visible === false) continue;

      if (el.kind === "image") {
        desiredImageIds.add(el.id);
        const src = (el as any).src as string | undefined;
        // Skip empty or placeholder sources
        if (!src || src.trim() === '' || src.startsWith('{{')) continue;

        const existing = runtime.current.imageMap.get(el.id);
        const existingSrc = existing ? ((existing as any).__vbSrc as string | undefined) : undefined;
        if (existing && existingSrc === src) continue;

        const img = new window.Image();
        (img as any).__vbSrc = src;
        img.crossOrigin = "anonymous";
        img.onload = () => draw();
        img.onerror = () => draw();
        img.src = src;
        runtime.current.imageMap.set(el.id, img);
      } else if (el.kind === "video") {
        const src = (el as any).src as string | undefined;
        // Skip empty or placeholder sources
        if (!src || src.trim() === '' || src.startsWith('{{')) continue;

        validVideoIds.add(el.id);
        if (hotVideoIds.has(el.id)) {
          ensureVideo(el.id, src);
        } else {
          const parked = parkedVideoMapRef.current.get(el.id);
          if (parked) {
            (parked as HTMLVideoElement & { __vbSrc?: string }).__vbSrc = src;
          } else {
            const active = runtime.current.videoMap.get(el.id);
            if (active) {
              (active as HTMLVideoElement & { __vbSrc?: string }).__vbSrc = src;
              parkVideo(el.id);
            }
          }
        }
      }
    }

    // Cleanup removed media.
    for (const [id, img] of runtime.current.imageMap.entries()) {
      if (!desiredImageIds.has(id)) {
        runtime.current.imageMap.delete(id);
        try { (img as any).onload = null; (img as any).onerror = null; } catch {}
      }
    }
    for (const [id, v] of runtime.current.videoMap.entries()) {
      if (!validVideoIds.has(id)) {
        runtime.current.videoMap.delete(id);
        try { v.pause(); } catch {}
        try { v.removeAttribute("src"); v.load(); } catch {}
        continue;
      }
      if (!hotVideoIds.has(id)) {
        parkVideo(id);
      }
    }
    for (const [id, v] of parkedVideoMapRef.current.entries()) {
      if (!validVideoIds.has(id)) {
        parkedVideoMapRef.current.delete(id);
        try { v.pause(); } catch {}
        try { v.removeAttribute("src"); v.load(); } catch {}
      }
    }

	    // Cleanup unused audio elements
    cleanupUnusedAudioElements(assets as any, runtime.current.audioMap);

	    draw();
	  }, [assets, elements, previewVideoHotSetPlan.hotIds]);

	  const elementsRef = useRef<Element[]>(elements);
	  useEffect(() => {
	    elementsRef.current = elements;
	  }, [elements]);

	  const clipRangesRef = useRef<Map<string, ClipRange>>(clipRangesByElementId);
	  useEffect(() => {
	    clipRangesRef.current = clipRangesByElementId;
	  }, [clipRangesByElementId]);

	  const animatedNodeCacheRef = useRef<Map<string, Konva.Node>>(new Map());
	  // Store transition effects for each element (for slide/zoom/wipe animations)
	  const transitionEffectsRef = useRef<Map<string, TransitionEffect>>(new Map());
    const fxRenderPacketsRef = useRef<Map<string, FxRenderPacket[]>>(new Map());
    const fxNodeBaseStateRef = useRef<Map<string, FxNodeBaseState>>(new Map());
    const fxAudioAnalyzerRef = useRef<FxAudioFeatureAnalyzer | null>(null);
    const fxBackgroundOverlayRef = useRef<FxBackgroundOverlayMap>(new Map());
    const fxBodyTrackerRef = useRef<FxBodyTracker | null>(null);
    const fxBodySegmenterRef = useRef<FxBodySegmenter | null>(null);
    const timelineVisibilityRef = useRef<Record<string, boolean>>({});
    const visibilityReactSyncMsRef = useRef(0);
    const fxOverlayMetricsRef = useRef({
      gpuTargets: 0,
      videoLayers: 0,
      videoCompositorPasses: 0,
      requestedVideoLayers: 0,
      droppedVideoLayers: 0,
      videoBudgetMode: "full" as FxVideoOverlayBudgetMode,
    });
    const timelineDensityModeRef = useRef<TimelineDensityMode>(getStoredTimelineDensityMode());
    const draftPreviewModeRef = useRef<DraftPreviewMode>(getStoredDraftPreviewMode());
    const fxBaseQualityTierRef = useRef(getFxSegmentationRuntimeQualityTier());
    const fxAdaptiveTierRef = useRef(fxBaseQualityTierRef.current);
    const fxAdaptiveTierControllerRef = useRef(
      new FxQualityTierController(fxBaseQualityTierRef.current)
    );
    const fxFpsTelemetryRef = useRef(new FxFpsTelemetry("preview"));
    const fxFrameMsRef = useRef(16.7);
    const fxSegmentationTargetsRef = useRef(0);
    const bodyFxRuntimeScanEnabled = isBodyFxRuntimeScanEnabled();
    const fxGpuOverlayRef = useRef<FxGpuOverlayMap>(new Map());
    const fxVideoOverlayRef = useRef<FxVideoOverlayMap>(new Map());
    const prevPlayheadSyncRef = useRef(playhead);
    const discontinuityLogRef = useRef(0);
    const fxSyncLogRef = useRef<{ lastMs: number; lastSummary: string }>({
      lastMs: 0,
      lastSummary: "",
    });

    useEffect(() => {
      const syncTier = () => {
        const nextBaseTier = getFxSegmentationRuntimeQualityTier();
        fxBaseQualityTierRef.current = nextBaseTier;
        fxAdaptiveTierControllerRef.current = new FxQualityTierController(nextBaseTier);
        fxAdaptiveTierRef.current = nextBaseTier;
      };
      syncTier();
      return subscribeFxSegmentationTuning(syncTier);
    }, []);

    useEffect(() => {
      timelineDensityModeRef.current = getStoredTimelineDensityMode();
      return subscribeTimelineDensityMode((mode) => {
        timelineDensityModeRef.current = mode;
      });
    }, []);

    useEffect(() => {
      draftPreviewModeRef.current = getStoredDraftPreviewMode();
      return subscribeDraftPreviewMode((mode) => {
        draftPreviewModeRef.current = mode;
      });
    }, []);
	  const rebuildAnimatedNodeCache = useCallback(() => {
	    const layer = layerRef.current as Konva.Layer | null;
	    if (!layer) return;
	    const ranges = clipRangesRef.current;
	    const next = new Map<string, Konva.Node>();
	    for (const el of elementsRef.current) {
	      if (!ranges.has(el.id)) continue;
	      const node = layer.findOne(`#node-${el.id}`) as Konva.Node | null;
	      if (node) next.set(el.id, node);
	    }
	    animatedNodeCacheRef.current = next;
	  }, [layerRef]);

	  useEffect(() => {
	    rebuildAnimatedNodeCache();
	  }, [rebuildAnimatedNodeCache, elements, clipRangesByElementId]);

	  // Keep canvas composition in sync with playhead (clip visibility + video seeks).
	  // Note: Audio muting/volume is now handled by syncVideoAudioAtPlayhead below.
	  useEffect(() => {
      const prevPlayhead = prevPlayheadSyncRef.current;
      const discontinuity = detectPlayheadDiscontinuity({
        playhead,
        prevPlayhead,
        isPlaying,
        loopPlayback,
        loopRegion,
        durationSec,
      });
      prevPlayheadSyncRef.current = playhead;
      const isSoftDiscontinuity =
        discontinuity.kind === "loop-wrap-soft" || discontinuity.kind === "playback-hitch-soft";
      const isHardSeekDiscontinuity = discontinuity.kind === "seek-hard";
      const shouldHandleDiscontinuity = isSoftDiscontinuity || isHardSeekDiscontinuity;
      const nowMs = performance.now();
      if (shouldHandleDiscontinuity && nowMs - discontinuityLogRef.current >= 150) {
        discontinuityLogRef.current = nowMs;
        try {
          console.info(
            `[FX Preview] discontinuity=${discontinuity.kind} playhead=${playhead.toFixed(
              3
            )} prev=${prevPlayhead.toFixed(3)} delta=${discontinuity.delta.toFixed(3)}`
          );
        } catch {}
      }
      if (shouldHandleDiscontinuity) {
        resetVideoSyncState();
        resetAudioSyncState();
      }

      const frameStartMs = performance.now();
      const currentFxQualityTier = fxAdaptiveTierControllerRef.current.update({
        baseTier: fxBaseQualityTierRef.current,
        isPlaying,
        frameMs: fxFrameMsRef.current,
        densityMode: timelineDensityModeRef.current,
        activeSegmentationTargets: fxSegmentationTargetsRef.current,
        nowMs: frameStartMs,
      });
      fxAdaptiveTierRef.current = currentFxQualityTier;
	    const visibilityChanges: Record<string, boolean> = {};

    syncVideosAtPlayhead(
      playhead,
      clips as any,
      assets as any,
      runtime.current.videoMap,
      (elementId, opacity) => {
        // Important: do NOT overwrite element.opacity here (it breaks opacity animations/effects).
        // Use timelineVisible as the gating signal instead.
        visibilityChanges[elementId] = opacity > 0;
      },
      isPlaying,
      undefined,
      {
        skipElementIds: webCodecs.enabled ? webCodecs.skipElementIds : undefined,
        transitions: transitions as any, // TRANSITION FIX: Enable video sync for incoming clips in transition windows
        // Apply effective mute state before first play() so the browser's
        // autoplay grant matches the final audible state (prevents the
        // first-play-silent bug).
        tracks: tracks as any,
        masterMuted,
      }
    );

	    // Compute transition effects using new clean system
	    const clipTimings: ClipTiming[] = (clips as any[]).map((c: any) => ({
	      id: c.id,
	      start: c.start,
	      end: c.end,
	      elementId: c.elementId,
	    }));
	    const currentlyInGap = isPlayheadInGap(clipTimings, playhead);

      const activeFxBlocks = getActiveFxBlocksAtTime(playhead);
      fxRenderPacketsRef.current = computeFxRenderPackets({
        playhead,
        clips: clips as any,
        tracks: tracks as any,
        fxBlocks: activeFxBlocks,
        qualityTier: currentFxQualityTier,
      });
      const activeBlockSummary = activeFxBlocks
        .slice(0, 6)
        .map((b) => `${b.id}(${b.start.toFixed(3)}-${b.end.toFixed(3)})`)
        .join(",");
      const summary = `${activeFxBlocks.length}:${activeBlockSummary}`;
      if (summary !== fxSyncLogRef.current.lastSummary || frameStartMs - fxSyncLogRef.current.lastMs >= 1000) {
        fxSyncLogRef.current = { lastMs: frameStartMs, lastSummary: summary };
        try {
          console.info(
            `[FX Preview] playhead=${playhead.toFixed(3)} activeBlocks=${activeFxBlocks.length} blocks=${activeBlockSummary || "none"}`
          );
        } catch {}
      }

	    transitionEffectsRef.current.clear();
    for (const clip of clipTimings) {
      if (!clip.elementId) continue;
      const { transition, otherClip } = findTransitionForClip(clip, transitions as any, clipTimings);
      const effect = getClipTransitionEffect(clip, playhead, transition, otherClip, canvasSize.width, canvasSize.height);
      if (effect) {
        transitionEffectsRef.current.set(clip.elementId, effect as any);
      }
      // Determine visibility:
      // - If clip has a transition effect, it MUST be visible (renderer-level rule)
      // - Otherwise, use normal clip bounds
      // This ensures both clips render during transition window without visibility hacks
      const hasTransitionEffect = effect !== null;
      const normallyVisible = playhead + PLAYHEAD_WINDOW_EPS >= clip.start && playhead <= clip.end + PLAYHEAD_WINDOW_EPS;
      const visible = hasTransitionEffect || normallyVisible;
      visibilityChanges[clip.elementId] = visible && !currentlyInGap;
    }

	    if (currentlyInGap) {
	      transitionEffectsRef.current.clear();
        fxRenderPacketsRef.current.clear();
	    }

    // Sync video audio (mute/volume) based on clip and track settings
    syncVideoAudioAtPlayhead(
      playhead,
      clips as any,
      assets as any,
      tracks as any,
      runtime.current.videoMap,
      masterMuted,
      isPlaying,
      undefined,
      webCodecs.enabled ? { skipElementIds: webCodecs.skipElementIds } : undefined
    );

    // Sync audio-only clips (mp3, wav, etc.) - this enables audio track playback
    syncAudioClipsAtPlayhead(
      playhead,
      clips as any,
      assets as any,
      tracks as any,
      runtime.current.audioMap,
      masterMuted,
      isPlaying
    );

    // GHOST ELEMENT FIX: Pass all element IDs to ensure orphaned elements are hidden
    const allElementIds = elementsRef.current.map(el => el.id);
    syncElementVisibilityAtPlayhead(
      playhead,
      clips as any,
      assets as any,
      (elementId, visible) => {
        visibilityChanges[elementId] = visible;
      },
      allElementIds
    );

    // GHOST ELEMENT FIX: Update ALL elements, not just those in visibilityChanges.
    // Throttle React state writes while playing; Konva visibility is already updated imperatively.
    const shouldSyncReactVisibility =
      !isPlaying || frameStartMs - visibilityReactSyncMsRef.current >= 160;
    if (shouldSyncReactVisibility) {
      visibilityReactSyncMsRef.current = frameStartMs;
      setElements((prev) => {
        let touched = false;
        const next = prev.map((e) => {
          // If element is in visibilityChanges, use that value
          // Otherwise, check if it's a canvas-only element (never bound to timeline)
          let nextVisible: boolean;
          if (e.id in visibilityChanges) {
            nextVisible = visibilityChanges[e.id];
          } else {
            // Canvas-only elements should remain visible during preview.
            // Elements not in visibilityChanges were never bound to timeline clips,
            // so they're canvas-only (backgrounds, standalone images/shapes/text).
            const hasNoClips = !clips.some((c: any) => c.elementId === e.id);
            nextVisible = hasNoClips ? (e.visible ?? true) : false;
          }

          const currTimelineVisible = e.timelineVisible ?? true;
          if (nextVisible === currTimelineVisible) return e;
          touched = true;
          const out: any = { ...e, timelineVisible: nextVisible };
          return out as Element;
        });
        return touched ? next : prev;
      });
    }

      timelineVisibilityRef.current = visibilityChanges;
		    markDirty();
      const frameElapsedMs = performance.now() - frameStartMs;
      fxFrameMsRef.current = fxFrameMsRef.current * 0.82 + frameElapsedMs * 0.18;
      fxFpsTelemetryRef.current.sample({
        frameMs: frameElapsedMs,
        qualityTier: currentFxQualityTier,
        activeSegmentationTargets: fxSegmentationTargetsRef.current,
        activeGpuOverlayTargets: fxOverlayMetricsRef.current.gpuTargets,
        activeVideoOverlayLayers: fxOverlayMetricsRef.current.videoLayers,
        activeVideoOverlayCompositorPasses: fxOverlayMetricsRef.current.videoCompositorPasses,
        requestedVideoOverlayLayers: fxOverlayMetricsRef.current.requestedVideoLayers,
        droppedVideoOverlayLayers: fxOverlayMetricsRef.current.droppedVideoLayers,
        videoOverlayBudgetMode: fxOverlayMetricsRef.current.videoBudgetMode,
        nowMs: performance.now(),
      });
	  }, [
      assets,
      clips,
      tracks,
      isPlaying,
      playhead,
      masterMuted,
      loopPlayback,
      loopRegion,
      durationSec,
      getActiveFxBlocksAtTime,
    ]);

	  // Apply FX packets/overlays in Preview to mirror Editor2 timeline shell behavior.
	  useEffect(() => {
	    const layer = layerRef.current as Konva.Layer | null;
	    if (!layer) return;
      const frameStartMs = performance.now();
	    if (animatedNodeCacheRef.current.size === 0) rebuildAnimatedNodeCache();

      let touched = false;
      for (const [id, baseState] of fxNodeBaseStateRef.current.entries()) {
        const node = animatedNodeCacheRef.current.get(id);
        if (!node) continue;
        touched = restoreFxNodeState({ node, baseState }) || touched;
      }
      fxNodeBaseStateRef.current.clear();

      const audioFeatures = (() => {
        if (!fxAudioAnalyzerRef.current) {
          fxAudioAnalyzerRef.current = new FxAudioFeatureAnalyzer();
        }
        return fxAudioAnalyzerRef.current.sample(runtime.current);
      })();

      const bodyTrackingStateByElementId = new Map<string, ReturnType<FxBodyTracker["getState"]>>();
      const bodySegmentationStateByElementId = new Map<string, ReturnType<FxBodySegmenter["getState"]>>();

      if (bodyFxRuntimeScanEnabled) {
        const trackingTargets: string[] = [];
        const segmentationTargets: string[] = [];
        let forceDraftSegmentation = false;
        for (const [elementId, packets] of fxRenderPacketsRef.current.entries()) {
          if (!packets?.length) continue;
          if (packets.some((packet) => packetNeedsBodyTracking(packet))) {
            trackingTargets.push(elementId);
          }
          if (packets.some((packet) => packetIsBackgroundBlur(packet) && packetWantsSegmentation(packet))) {
            segmentationTargets.push(elementId);
            if (
              packets.some(
                (packet) =>
                  packetIsBackgroundBlur(packet) &&
                  packetWantsSegmentation(packet) &&
                  Number.isFinite(packet.params?.segmentationTier) &&
                  Number(packet.params.segmentationTier) <= 0.5
              )
            ) {
              forceDraftSegmentation = true;
            }
          }
        }
        fxSegmentationTargetsRef.current = segmentationTargets.length;

        if (trackingTargets.length > 0) {
          if (!fxBodyTrackerRef.current) {
            fxBodyTrackerRef.current = new FxBodyTracker();
          }
          fxBodyTrackerRef.current.tick({
            videoMap: runtime.current.videoMap as Map<string, HTMLVideoElement>,
            targetElementIds: trackingTargets,
          });
          for (const elementId of trackingTargets) {
            bodyTrackingStateByElementId.set(elementId, fxBodyTrackerRef.current.getState(elementId));
          }
        } else {
          fxBodyTrackerRef.current?.reset();
        }

        if (segmentationTargets.length > 0) {
          if (!fxBodySegmenterRef.current) {
            fxBodySegmenterRef.current = new FxBodySegmenter();
          }
          const baseSegmentationTier: FxQualityTier =
            fxAdaptiveTierRef.current === "draft" ? "draft" : "balanced";
          const segmentationQualityTier: FxQualityTier = forceDraftSegmentation
            ? "draft"
            : baseSegmentationTier;
          fxBodySegmenterRef.current.tick({
            videoMap: runtime.current.videoMap as Map<string, HTMLVideoElement>,
            targetElementIds: segmentationTargets,
            qualityTier: segmentationQualityTier,
            force: !isPlaying,
            nowMs: frameStartMs,
          });
          for (const elementId of segmentationTargets) {
            bodySegmentationStateByElementId.set(elementId, fxBodySegmenterRef.current.getState(elementId));
          }
        } else {
          fxBodySegmenterRef.current?.reset();
        }
      } else {
        fxBodyTrackerRef.current?.reset();
        fxBodySegmenterRef.current?.reset();
        fxSegmentationTargetsRef.current = 0;
      }

      const activeBackgroundOverlayIds = new Set<string>();
      const activeGpuOverlayIds = new Set<string>();
      const activeVideoOverlayKeys = new Set<string>();
      let frameActiveGpuOverlayTargets = 0;
      let frameActiveVideoOverlayLayers = 0;
      let frameActiveVideoOverlayCompositorPasses = 0;
      let frameRequestedVideoOverlayLayers = 0;
      let frameDroppedVideoOverlayLayers = 0;
      const draftPreviewMode = draftPreviewModeRef.current;
      const overlayPlaybackBudget = resolveFxVideoOverlayBudget({
        isPlaying,
        draftPreviewMode,
        qualityTier: fxAdaptiveTierRef.current,
        frameMs: fxFrameMsRef.current,
        activeSegmentationTargets: fxSegmentationTargetsRef.current,
      });
      let remainingVideoOverlayLayers =
        typeof overlayPlaybackBudget.maxRenderedLayers === "number" &&
        Number.isFinite(overlayPlaybackBudget.maxRenderedLayers)
        ? Math.max(0, Number(overlayPlaybackBudget.maxRenderedLayers))
        : Number.POSITIVE_INFINITY;
      const overlayRenderScale =
        isPlaying
          ? draftPreviewMode === "off"
            ? 1
            : draftPreviewMode === "on"
              ? 0.66
              : fxAdaptiveTierRef.current === "draft"
                ? 0.66
                : timelineDensityModeRef.current === "BAL"
                  ? 0.82
                  : 1
          : 1;
      for (const [elementId, packets] of fxRenderPacketsRef.current.entries()) {
        if (!packets || packets.length === 0) continue;
        if (timelineVisibilityRef.current[elementId] === false) continue;

        const node = animatedNodeCacheRef.current.get(elementId);
        if (!node) continue;

        const baseState = captureFxNodeBaseState(node);
        fxNodeBaseStateRef.current.set(elementId, baseState);
        touched =
          applyFxPacketsToNode({
            node,
            packets,
            playhead,
            baseState,
            audioFeatures,
            bodyTrackingState: bodyTrackingStateByElementId.get(elementId) ?? null,
          }) || touched;

        const backgroundOverlaySync = syncBackgroundBlurOverlay({
          elementId,
          node,
          packets,
          bodyTrackingState: bodyTrackingStateByElementId.get(elementId) ?? null,
          bodySegmentationState: bodySegmentationStateByElementId.get(elementId) ?? null,
          overlayMap: fxBackgroundOverlayRef.current,
          renderWidth: canvasSize.width,
          renderHeight: canvasSize.height,
          playhead,
          nowMs: frameStartMs,
        });
        if (backgroundOverlaySync) {
          activeBackgroundOverlayIds.add(elementId);
          touched = true;
        }

        const gpuOverlaySync = syncFxGpuOverlay({
          elementId,
          node,
          packets,
          overlayMap: fxGpuOverlayRef.current,
        });
        if (gpuOverlaySync.active) {
          activeGpuOverlayIds.add(elementId);
          frameActiveGpuOverlayTargets += 1;
          touched = true;
        }

        const videoOverlaySync = syncFxVideoOverlays({
          elementId,
          node,
          packets,
          overlayMap: fxVideoOverlayRef.current,
          renderScale: overlayRenderScale,
          maxRenderedLayers: remainingVideoOverlayLayers,
          isPlaying,
        });
        if (videoOverlaySync.activeKeys.size > 0) {
          for (const key of videoOverlaySync.activeKeys) activeVideoOverlayKeys.add(key);
          frameActiveVideoOverlayLayers += videoOverlaySync.renderedLayerCount;
          frameActiveVideoOverlayCompositorPasses += videoOverlaySync.compositorPasses;
          remainingVideoOverlayLayers = Math.max(
            0,
            remainingVideoOverlayLayers - videoOverlaySync.renderedLayerCount
          );
          touched = true;
        }
        frameRequestedVideoOverlayLayers += videoOverlaySync.requestedLayerCount;
        frameDroppedVideoOverlayLayers += videoOverlaySync.droppedLayerCount;
      }

      touched =
        cleanupBackgroundBlurOverlays({
          overlayMap: fxBackgroundOverlayRef.current,
          keepElementIds: activeBackgroundOverlayIds,
          nowMs: frameStartMs,
        }) || touched;
      touched =
        cleanupFxGpuOverlays({
          overlayMap: fxGpuOverlayRef.current,
          keepElementIds: activeGpuOverlayIds,
        }) || touched;
      touched =
        cleanupFxVideoOverlays({
          overlayMap: fxVideoOverlayRef.current,
          keepKeys: activeVideoOverlayKeys,
        }) || touched;

      fxOverlayMetricsRef.current = {
        gpuTargets: frameActiveGpuOverlayTargets,
        videoLayers: frameActiveVideoOverlayLayers,
        videoCompositorPasses: frameActiveVideoOverlayCompositorPasses,
        requestedVideoLayers: frameRequestedVideoOverlayLayers,
        droppedVideoLayers: frameDroppedVideoOverlayLayers,
        videoBudgetMode: overlayPlaybackBudget.mode,
      };

	    if (touched || isPlaying) markDirty();
	  }, [
      playhead,
      isPlaying,
      rebuildAnimatedNodeCache,
      bodyFxRuntimeScanEnabled,
      canvasSize.width,
      canvasSize.height,
    ]);

	  // Playback clock (Timeline UI isn't mounted in this route).
	  const playheadRef = useRef(playhead);
	  useEffect(() => {
    playheadRef.current = playhead;
  }, [playhead]);

  useEffect(() => {
    if (!isPlaying) return;
    if (durationSec <= 0) return;

    let last = Number.NaN;
    const owner = "vb:studio:preview";

    const tick = (now: number) => {
      if (!Number.isFinite(last)) {
        last = now;
        return;
      }

      const rawDt = Math.max(0, (now - last) / 1000);
      const dt = Math.min(rawDt, 0.25);
      last = now;
      if (rawDt - dt > 0.001) {
        try {
          console.debug(
            `[FX Preview] clamped-dt raw=${rawDt.toFixed(3)} used=${dt.toFixed(3)} playhead=${playheadRef.current.toFixed(
              3
            )}`
          );
        } catch {}
      }

      const loopEnabled = !!loopPlayback;
      const loopStart =
        loopEnabled && loopRegion && loopRegion.end > loopRegion.start + 0.01
          ? clamp(loopRegion.start, 0, durationSec)
          : 0;
      const loopEnd =
        loopEnabled && loopRegion && loopRegion.end > loopRegion.start + 0.01
          ? clamp(loopRegion.end, 0, durationSec)
          : durationSec;
      const hasLoopRange = loopEnabled && loopEnd > loopStart + 0.01;

      let cur = playheadRef.current;
      if (hasLoopRange && (cur < loopStart || cur >= loopEnd)) cur = loopStart;

      let next = cur + dt;
      if (hasLoopRange) {
        if (next >= loopEnd) {
          const span = Math.max(0.001, loopEnd - loopStart);
          next = loopStart + ((next - loopEnd) % span);
        }
      } else if (next >= durationSec) {
        if (loopPlayback) {
          next = 0;
        } else {
          next = durationSec;
          setPlaying(false);
        }
      }
      playheadRef.current = next;
      setPlayhead(next);

      // Force redraw to keep videos responsive in Konva.
      markDirty();

      // No direct RAF scheduling here; a singleton RAF clock drives this tick.
    };

    const started = requestSingletonRafLoop(owner, tick);
    return () => {
      if (started) releaseSingletonRafLoop(owner);
      else cancelSingletonRafRequest(owner);
    };
  }, [durationSec, isPlaying, loopPlayback, loopRegion, setPlayhead, setPlaying]);

  const previewRootRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    onFs();
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        return;
      }
      const el = previewRootRef.current ?? document.documentElement;
      await (el as any).requestFullscreen?.();
    } catch (err) {
      console.warn("[preview] Failed to toggle fullscreen:", err);
    }
  }, []);

  const togglePlay = useCallback(() => {
    if (durationSec <= 0) return;
    if (!isPlaying && playhead >= durationSec - 0.001) {
      setPlayhead(0);
      playheadRef.current = 0;
    }
    setPlaying(!isPlaying);
  }, [durationSec, isPlaying, playhead, setPlayhead, setPlaying]);

  const closeWindow = useCallback(() => {
    try { setPlaying(false); } catch {}
    // If this was opened as a popup, this will succeed; otherwise fallback to studio.
    window.close();
    if (!window.closed) window.location.assign("/studio");
  }, [setPlaying]);

  // Keyboard shortcuts: Space play/pause, Esc close.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeWindow();
        return;
      }
      if (e.key === " " || e.code === "Space") {
        e.preventDefault();
        togglePlay();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeWindow, togglePlay]);

  const scrubWasPlayingRef = useRef(false);
  const scrubTrackRef = useRef<HTMLDivElement>(null);
  const scrubToClientX = useCallback((clientX: number) => {
    const track = scrubTrackRef.current;
    if (!track) return;
    if (durationSec <= 0) return;
    const r = track.getBoundingClientRect();
    const p = clamp((clientX - r.left) / Math.max(1, r.width), 0, 1);
    const t = p * durationSec;
    playheadRef.current = t;
    setPlayhead(t);
    markDirty();
  }, [durationSec, setPlayhead]);

  const onScrubPointerDown = useCallback((e: React.PointerEvent) => {
    if (durationSec <= 0) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    scrubWasPlayingRef.current = isPlaying;
    setPlaying(false);
    scrubToClientX(e.clientX);
  }, [durationSec, isPlaying, scrubToClientX, setPlaying]);

  const onScrubPointerMove = useCallback((e: React.PointerEvent) => {
    if (!(e.currentTarget as HTMLElement).hasPointerCapture(e.pointerId)) return;
    scrubToClientX(e.clientX);
  }, [scrubToClientX]);

  const onScrubPointerUp = useCallback((e: React.PointerEvent) => {
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch {}
    if (scrubWasPlayingRef.current) setPlaying(true);
  }, [setPlaying]);

  const progress = durationSec > 0 ? clamp(playhead / durationSec, 0, 1) : 0;

  const [controlsVisible, setControlsVisible] = useState(true);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showControls = useCallback(() => {
    setControlsVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (isPlaying) {
      hideTimerRef.current = setTimeout(() => setControlsVisible(false), 3000);
    }
  }, [isPlaying]);

  useEffect(() => {
    if (!isPlaying) {
      setControlsVisible(true);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    } else {
      hideTimerRef.current = setTimeout(() => setControlsVisible(false), 3000);
    }
    return () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); };
  }, [isPlaying]);

  return (
    <div
      ref={previewRootRef}
      className="fixed inset-0 bg-neutral-950 text-white select-none"
      onMouseMove={showControls}
      onPointerDown={showControls}
      style={{ cursor: controlsVisible ? 'default' : 'none' }}
    >
      {/* Top bar — close + info */}
      <div
        className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-5 pt-4 pb-8 transition-opacity duration-300"
        style={{
          opacity: controlsVisible ? 1 : 0,
          pointerEvents: controlsVisible ? 'auto' : 'none',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, transparent 100%)',
        }}
      >
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-sm font-medium text-white/80 tracking-wide">Preview</span>
        </div>
        <button
          type="button"
          onClick={closeWindow}
          className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center transition-colors"
          aria-label="Close preview"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Canvas area */}
      <div ref={videoHostRef} className="absolute inset-0 flex items-center justify-center">
        <CanvasStage
          layout="plain"
          stageRef={stageRef}
          layerRef={layerRef}
          canvasW={canvasSize.width}
          canvasH={canvasSize.height}
          scale={scale}
          stagePos={{ x: 0, y: 0 }}
          isSelecting={false}
          elements={elements}
          selectedIds={[]}
          runtime={runtime}
          onZoom={() => {}}
          onStageMouseDown={() => {}}
          onStageMouseMove={() => {}}
          onStageMouseUp={() => {}}
          onNodeDragEnd={() => {}}
          onNodeTransformEnd={() => {}}
          onSelectElement={() => {}}
          updateEl={() => {}}
          selectionRect={{ x: 0, y: 0, width: 0, height: 0, visible: false }}
          selRectRef={selRectRef}
          trRef={trRef}
          showGuides={false}
          playhead={playhead}
          cameraTransform={cameraTransform}
          cameraTrack={cameraTrack ?? undefined}
	          cameraMotionV2Enabled={allowCameraMotionV2}
	          previewMode
	          clips={clips as any}
            transitionEffectsRef={transitionEffectsRef}
	        />

        {/* Center play overlay */}
        {!isPlaying && durationSec > 0 && (
          <button
            type="button"
            onClick={togglePlay}
            className="absolute inset-0 grid place-items-center z-10"
            aria-label="Play"
          >
            <span className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 grid place-items-center shadow-2xl transition-transform hover:scale-110 active:scale-95">
              <Play className="w-7 h-7 text-white ml-0.5" fill="white" />
            </span>
          </button>
        )}
      </div>

      {/* Bottom controls */}
      <div
        className="absolute left-0 right-0 bottom-0 z-20 px-4 pb-4 transition-opacity duration-300"
        style={{
          opacity: controlsVisible ? 1 : 0,
          pointerEvents: controlsVisible ? 'auto' : 'none',
          background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
        }}
      >
        {/* Scrub track */}
        <div className="group px-1 mb-3">
          <div
            ref={scrubTrackRef}
            className="relative w-full h-1 group-hover:h-1.5 rounded-full cursor-pointer transition-all"
            style={{ background: 'rgba(255,255,255,0.15)' }}
            onPointerDown={onScrubPointerDown}
            onPointerMove={onScrubPointerMove}
            onPointerUp={onScrubPointerUp}
          >
            {/* Played portion */}
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-white"
              style={{ width: `${(progress * 100).toFixed(4)}%` }}
            />
            {/* Scrub handle */}
            <div
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ left: `calc(${(progress * 100).toFixed(4)}% - 6px)` }}
            />
          </div>
        </div>

        {/* Controls row */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            {/* Play / Pause */}
            <button
              type="button"
              onClick={togglePlay}
              className="w-9 h-9 rounded-full hover:bg-white/10 grid place-items-center transition-colors"
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying
                ? <Pause className="w-5 h-5" fill="white" />
                : <Play className="w-5 h-5 ml-0.5" fill="white" />}
            </button>

            {/* Volume */}
            <button
              type="button"
              onClick={() => setMasterMuted(!masterMuted)}
              className="w-9 h-9 rounded-full hover:bg-white/10 grid place-items-center transition-colors"
              aria-label={masterMuted ? "Unmute" : "Mute"}
            >
              {masterMuted ? <VolumeX className="w-5 h-5 text-white/60" /> : <Volume2 className="w-5 h-5" />}
            </button>

            {/* Time */}
            <span className="text-xs tabular-nums text-white/70 ml-1">
              {formatTime(playhead)}<span className="text-white/30 mx-1">/</span>{formatTime(durationSec)}
            </span>
          </div>

          {/* Fullscreen */}
          <button
            type="button"
            onClick={toggleFullscreen}
            className="w-9 h-9 rounded-full hover:bg-white/10 grid place-items-center transition-colors"
            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
          </button>
        </div>

        {/* Hidden-but-wired loop state */}
        <div className="sr-only" aria-hidden>
          {String(loopPlayback)}
          {String((tracks || []).length)}
        </div>
      </div>
    </div>
  );
}

export default function StudioPreviewPage() {
  return (
    <TimelineProvider>
      <StudioPreviewInner />
    </TimelineProvider>
  );
}
